/**
 * Aparición progresiva al hacer scroll.
 *
 * Solo activa la clase: el estado inicial, la variante y la duración las
 * define el CSS (css/utilities/animations.css). Acá se decide únicamente
 * *cuándo* aparece cada elemento y con cuánto retraso respecto de sus
 * hermanos.
 *
 * Si el sistema pide movimiento reducido, todo se marca visible de entrada.
 */

/** Porción del elemento que tiene que entrar para disparar la animación. */
const THRESHOLD = 0.08;
/**
 * Red de seguridad: pasado este tiempo desde la carga, todo lo que siga
 * oculto se muestra sí o sí.
 *
 * El contenido nunca puede depender de que una animación dispare. Si un
 * observer no llega a ejecutarse — por una variante de CSS que falla, un
 * navegador que se comporta distinto o un elemento que nunca entra en el
 * viewport — el texto tiene que verse igual. Antes de esto, cinco titulares
 * quedaban invisibles en producción.
 */
const FAILSAFE_MS = 4000;
/** Se dispara un poco antes de que llegue al borde inferior de la pantalla. */
const ROOT_MARGIN = '0px 0px -8% 0px';
/** Escalón entre hermanos, en ms. Coincide con --stagger del CSS. */
const STAGGER = 80;
/** Tope de escalones: con muchos hermanos, esperar de más se siente lento. */
const MAX_STEPS = 5;

/**
 * Retraso de un elemento según su posición entre los hermanos que también
 * se revelan. Un `data-reveal-delay` explícito en el HTML manda sobre esto.
 */
function delayFor(el) {
  if (el.dataset.revealDelay) return `${parseInt(el.dataset.revealDelay, 10) || 0}ms`;

  // Si el CSS ya fijó un delay propio para este elemento, no lo pisamos.
  if (el.style.getPropertyValue('--reveal-delay')) return null;

  const siblings = Array.from(el.parentElement?.children || []).filter((n) =>
    n.classList.contains('reveal')
  );
  const index = Math.min(siblings.indexOf(el), MAX_STEPS);
  return index > 0 ? `${index * STAGGER}ms` : null;
}

export function init() {
  const targets = document.querySelectorAll('.reveal');
  if (!targets.length) return;

  const showAll = () => targets.forEach((el) => el.classList.add('is-visible'));

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    showAll();
    return;
  }

  // Sin IntersectionObserver (navegadores viejos) mostramos todo: mejor sin
  // animación que con contenido invisible.
  if (!('IntersectionObserver' in window)) {
    showAll();
    return;
  }

  const io = new IntersectionObserver(
    (entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const el = entry.target;

        const delay = delayFor(el);
        if (delay) el.style.setProperty('--reveal-delay', delay);

        el.classList.add('is-visible');

        // Una sola vez: reaparecer al volver a subir marea y distrae.
        observer.unobserve(el);
      }
    },
    { threshold: THRESHOLD, rootMargin: ROOT_MARGIN }
  );

  targets.forEach((el) => io.observe(el));

  // Red de seguridad (ver FAILSAFE_MS). Lo que a esta altura siga sin
  // revelarse, se revela igual: es preferible perder la animación antes que
  // dejar contenido invisible.
  window.setTimeout(() => {
    let rescued = 0;
    targets.forEach((el) => {
      if (el.classList.contains('is-visible')) return;
      // Solo lo que ya está o estuvo cerca del viewport: lo que sigue muy
      // abajo se anima normalmente cuando el usuario llegue.
      const rect = el.getBoundingClientRect();
      if (rect.top > window.innerHeight * 1.5) return;
      el.classList.add('is-visible');
      io.unobserve(el);
      rescued++;
    });
    if (rescued) {
      console.warn(`[reveal] ${rescued} elemento(s) se mostraron por la red de seguridad.`);
    }
  }, FAILSAFE_MS);
}
